import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import type { MeResponse, SessionInfo } from "@handvero/shared";
import { api, errorText } from "../api";
import { GuideList } from "./Guide";
import { Icon, Message, useUnauthorizedRedirect } from "./ui";

/*
 * Mein Konto: Jede Person verwaltet hier selbst ihren Namen, ihr Passwort und ihre angemeldeten Geräte und kann
 * ihr Konto löschen. Der Inhaber löscht stattdessen die ganze Firma.
 */

const ROLE_TEXT = { owner: "Inhaber", admin: "Admin", employee: "Mitarbeiter" } as const;

function when(iso: string): string {
  const d = new Date(iso);
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 2) return "gerade eben";
  if (mins < 60) return `vor ${mins} Minuten`;
  if (mins < 60 * 24) return `heute, ${d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })} Uhr`;
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function Block({ num, title, text, children, danger }: { num: string; title: string; text?: ReactNode; children: ReactNode; danger?: boolean }) {
  return (
    <section className={`acc-block${danger ? " danger" : ""}`}>
      <header>
        <span className="acc-num">{num}</span>
        <div>
          <h2>{title}</h2>
          {text && <p>{text}</p>}
        </div>
      </header>
      <div className="acc-content">{children}</div>
    </section>
  );
}

export function AccountView({ me, onChanged }: { me: MeResponse; onChanged?: () => void }) {
  const onAuth = useUnauthorizedRedirect();
  const navigate = useNavigate();
  const isOwner = me.user.role === "owner";
  const inSupport = !!me.support;

  const [name, setName] = useState(me.user.name);
  const [nameMsg, setNameMsg] = useState<[string, "ok" | "bad"] | null>(null);

  const [pw, setPw] = useState({ current: "", next: "" });
  const [pwMsg, setPwMsg] = useState<[string, "ok" | "bad"] | null>(null);

  const [sessions, setSessions] = useState<SessionInfo[] | null>(null);
  const [sessMsg, setSessMsg] = useState("");

  const [del, setDel] = useState({ password: "", companyName: "", sure: false });
  const [delMsg, setDelMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [digest, setDigest] = useState<boolean | null>(null);
  const [digestMsg, setDigestMsg] = useState("");
  useEffect(() => {
    api.get<{ weeklyDigest: boolean }>("/api/reminders").then((r) => setDigest(r.weeklyDigest), () => setDigest(false));
  }, []);
  async function changeDigest(on: boolean) {
    setDigestMsg("");
    try {
      await api.put("/api/reminders", { weeklyDigest: on });
      setDigest(on);
    } catch (err) {
      setDigestMsg(errorText(err));
    }
  }

  const loadSessions = useCallback(() => {
    api
      .get<SessionInfo[]>("/api/account/sessions")
      .then(setSessions)
      .catch((err) => onAuth(err) || setSessMsg(errorText(err)));
  }, [onAuth]);
  useEffect(loadSessions, [loadSessions]);

  async function run(fn: () => Promise<void>, fail: (m: string) => void) {
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      if (!onAuth(err)) fail(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  function saveName(e: FormEvent) {
    e.preventDefault();
    void run(
      async () => {
        await api.patch("/api/account", { name });
        setNameMsg(["Gespeichert.", "ok"]);
        onChanged?.();
      },
      (m) => setNameMsg([m, "bad"]),
    );
  }

  function savePassword(e: FormEvent) {
    e.preventDefault();
    void run(
      async () => {
        await api.post("/api/account/password", pw);
        setPw({ current: "", next: "" });
        setPwMsg(["Neues Passwort gespeichert. Alle anderen Geräte sind jetzt abgemeldet.", "ok"]);
        loadSessions();
      },
      (m) => setPwMsg([m, "bad"]),
    );
  }

  function logoutDevice(s: SessionInfo) {
    void run(
      async () => {
        await api.del(`/api/account/sessions/${s.id}`);
        if (s.current) navigate(location.pathname.startsWith("/desktop") ? "/desktop/anmelden" : "/anmelden", { replace: true });
        else loadSessions();
      },
      setSessMsg,
    );
  }

  function logoutOthers() {
    void run(
      async () => {
        await api.post("/api/account/sessions/logout-others");
        loadSessions();
      },
      setSessMsg,
    );
  }

  function remove(e: FormEvent) {
    e.preventDefault();
    void run(
      async () => {
        if (isOwner) await api.del("/api/company", { password: del.password, companyName: del.companyName });
        else await api.del("/api/account", { password: del.password });
        window.location.assign("/?geloescht=1");
      },
      setDelMsg,
    );
  }

  const others = sessions?.filter((s) => !s.current && !s.support).length ?? 0;

  return (
    <div className="acc">
      {inSupport && (
        <p className="app-msg info">
          Der HandVero-Support sieht diese Seite, kann hier aber nichts ändern. Name, Passwort, Geräte und Löschen bleiben Ihnen vorbehalten.
        </p>
      )}

      <div className="acc-who">
        <span className="acc-avatar">{me.user.name.trim().charAt(0).toUpperCase() || "?"}</span>
        <div>
          <b>{me.user.name}</b>
          <small>
            {me.user.email} · {ROLE_TEXT[me.user.role]} bei {me.company.name}
          </small>
        </div>
      </div>

      <Block num="01" title="Ihr Name" text="So sehen Sie Ihre Kollegen bei Fotos, Notizen und Terminen.">
        <form className="app-form acc-inline" onSubmit={saveName}>
          <label>
            Vor- und Nachname
            <input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" disabled={inSupport} required />
          </label>
          <button className="app-btn small" disabled={busy || inSupport || name.trim() === me.user.name}>
            Speichern
          </button>
        </form>
        {nameMsg && <Message kind={nameMsg[1]}>{nameMsg[0]}</Message>}
      </Block>

      <Block num="02" title="Passwort" text="Mindestens 10 Zeichen. Nach dem Ändern werden alle anderen Geräte abgemeldet.">
        <form className="app-form" onSubmit={savePassword}>
          <div className="two">
            <label>
              Bisheriges Passwort
              <input type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} autoComplete="current-password" disabled={inSupport} required />
            </label>
            <label>
              Neues Passwort
              <input type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} autoComplete="new-password" minLength={10} disabled={inSupport} required />
            </label>
          </div>
          <button className="app-btn small" disabled={busy || inSupport || !pw.current || pw.next.length < 10}>
            Passwort ändern
          </button>
        </form>
        {pwMsg && <Message kind={pwMsg[1]}>{pwMsg[0]}</Message>}
      </Block>

      <Block num="03" title="Angemeldete Geräte" text="Ein verlorenes Handy oder ein fremder PC? Hier mit einem Klick abmelden.">
        {!sessions && !sessMsg && <div className="skel" role="status" aria-label="Wird geladen"><i /><i /></div>}
        {sessions && (
          <ul className="acc-devices">
            {sessions.map((s) => (
              <li key={s.id} className={s.support ? "support" : undefined}>
                <span className="acc-dev-ico">
                  <Icon name={s.support ? "shield" : /iPhone|Android|iPad/.test(s.device) ? "camera" : "grid"} />
                </span>
                <span className="acc-dev-text">
                  <b>
                    {s.device}
                    {s.current && <em>Dieses Gerät</em>}
                  </b>
                  <small>
                    Zuletzt aktiv {when(s.lastSeenAt)} · angemeldet seit {new Date(s.createdAt).toLocaleDateString("de-DE")}
                  </small>
                </span>
                {!s.support && (
                  <button className="app-btn small ghost" disabled={busy || inSupport} onClick={() => logoutDevice(s)}>
                    Abmelden
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        {others > 0 && (
          <button className="vero-link" disabled={busy || inSupport} onClick={logoutOthers}>
            Alle anderen Geräte abmelden ({others})
          </button>
        )}
        <Message>{sessMsg}</Message>
      </Block>

      <Block
        num="04"
        title="Abo und Paket"
        text={
          <>
            Sie nutzen „{me.company.planLabel}“. Ein kostenpflichtiges Abo gibt es derzeit nicht, deshalb muss auch nichts gekündigt werden.
          </>
        }
      >
        <Link className="app-btn small ghost" to="/kundenbereich">
          {isOwner || me.user.role === "admin" ? "Paket und Mitarbeiter im Kundenbereich" : "Zum Kundenbereich"}
        </Link>
      </Block>

      <Block
        num="05"
        title="Wochenübersicht per E-Mail"
        text="Montags früh eine kurze Mail mit Ihren Terminen der Woche und, falls es etwas gibt, überfälligen Rechnungen. Höchstens eine Mail pro Woche, nur wenn es etwas zu berichten gibt, und jederzeit abschaltbar. Standardmäßig ist sie aus."
      >
        <label className="check">
          <input type="checkbox" checked={digest === true} disabled={digest === null || inSupport} onChange={(e) => void changeDigest(e.target.checked)} />
          Wochenübersicht an {me.user.email} schicken
        </label>
        <Message>{digestMsg}</Message>
      </Block>

      <Block num="06" title="Rundgänge mit Bildern" text="Kurze Bildergeschichten, die erklären, wie HandVero und Ihre gebuchten Pakete funktionieren. Jederzeit wieder ansehen.">
        <GuideList billing={me.addons.billing} />
      </Block>

      {(isOwner || me.user.role === "admin") && (
        <Block
          num="07"
          title="Alle Daten herunterladen"
          text="Eine Sicherung als ZIP-Datei: alle Projekte mit Ordnern, Fotos und Dateien, die Notizen und Ihre Kundenliste. Gut vor größeren Änderungen und für Ihre eigene Sicherung."
        >
          <a className="app-btn small ghost" href="/api/company/export" download>
            ZIP-Datei herunterladen
          </a>
        </Block>
      )}

      <Block
        num="08"
        danger
        title={isOwner ? "Firma und Konto löschen" : "Konto löschen"}
        text={
          isOwner
            ? "Als Inhaber löschen Sie die ganze Firma: alle Projekte, Fotos, Dokumente, Notizen, Termine und alle Zugänge. Das lässt sich nicht rückgängig machen."
            : "Ihr Zugang wird gelöscht. Fotos und Dokumente, die Sie hochgeladen haben, bleiben in der Firma."
        }
      >
        {!del.sure ? (
          <button className="app-btn small danger" disabled={inSupport} onClick={() => setDel({ ...del, sure: true })}>
            {isOwner ? "Firma löschen …" : "Konto löschen …"}
          </button>
        ) : (
          <form className="app-form" onSubmit={remove}>
            {isOwner && (
              <label>
                Zur Sicherheit den Firmennamen eintippen: „{me.company.name}“
                <input value={del.companyName} onChange={(e) => setDel({ ...del, companyName: e.target.value })} autoComplete="off" required />
              </label>
            )}
            <label>
              Ihr Passwort
              <input type="password" value={del.password} onChange={(e) => setDel({ ...del, password: e.target.value })} autoComplete="current-password" required />
            </label>
            <div className="acc-actions">
              <button className="app-btn small danger" disabled={busy || !del.password}>
                {isOwner ? "Firma endgültig löschen" : "Konto endgültig löschen"}
              </button>
              <button type="button" className="app-btn small ghost" onClick={() => setDel({ password: "", companyName: "", sure: false })}>
                Abbrechen
              </button>
            </div>
          </form>
        )}
        <Message>{delMsg}</Message>
      </Block>

      <aside className="acc-safe">
        <Icon name="lock" />
        <div>
          <b>So schützt HandVero Ihr Konto</b>
          <p>
            Ihre Firma ist streng von anderen Firmen getrennt. Passwörter speichern wir nur als Prüfwert, nie im Klartext. Mitarbeiter sehen nur Projekte, für
            die sie freigegeben sind. Der HandVero-Support kommt nur mit Ihrer Erlaubnis und für begrenzte Zeit in Ihr Konto.
          </p>
        </div>
      </aside>
    </div>
  );
}
